import { useEffect, useState } from 'react';
import { TrendingUp, Headphones, Users, BarChart3, Clock, Loader2 } from 'lucide-react';

interface AnalyticsData {
  kpis: {
    totalListens: number;
    averageCompletionRate: number; // percentage
    activeUsers: number;
  };
  topStories: {
    id: string;
    title: string;
    listens: number;
    completionRate: number;
  }[];
}

const API_URL = import.meta.env.VITE_API_URL || 'https://heritage-os-api.uplaytv3117.workers.dev/api/v1';

export default function Analytics() {
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const token = localStorage.getItem('jwt_token');
        const response = await fetch(`${API_URL}/analytics/stats`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch analytics data');
        }

        const rawResult = await response.json();
        const result = Array.isArray(rawResult) ? rawResult[0] : rawResult;
        
        setData({
          kpis: {
            totalListens: result?.total_views || 0,
            averageCompletionRate: Math.round(result?.completion_rate || 0),
            activeUsers: result?.active_users || 0
          },
          topStories: (result.popular_content || []).map((item: any, index: number) => ({
            id: item.content_uuid || String(index),
            title: item.title || 'Untitled Content',
            listens: item.views || 0,
            completionRate: item.completion_rate || 0
          }))
        });
      } catch (err) {
        // Surface the failure instead of silently showing fabricated numbers -
        // an admin relying on this screen needs to know it isn't real data.
        console.error(err);
        setError(err instanceof Error ? err.message : 'Failed to load analytics data');
        setData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, []);

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center min-h-screen">
        <Loader2 className="animate-spin text-slate-400" size={32} />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="p-8 max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-semibold text-slate-900 tracking-tight">Analytics Dashboard</h1>
          <p className="text-slate-500 mt-2 text-sm">Monitor your platform's engagement and story performance.</p>
        </div>
        <div className="bg-white p-8 rounded-xl border border-red-200 shadow-sm text-center">
          <p className="text-red-600 font-medium mb-1">Couldn't load analytics data</p>
          <p className="text-slate-500 text-sm">{error || 'Unknown error'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-semibold text-slate-900 tracking-tight">Analytics Dashboard</h1>
        <p className="text-slate-500 mt-2 text-sm">Monitor your platform's engagement and story performance.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
          <div className="flex items-start justify-between mb-4">
            <div className="p-2 bg-slate-50 rounded-lg border border-slate-100">
              <Headphones className="text-slate-700" size={20} />
            </div>
            <span className="text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full flex items-center gap-1">
              <TrendingUp size={12} />
              +12%
            </span>
          </div>
          <div>
            <p className="text-sm font-medium text-slate-500 mb-1">Total Listens</p>
            <h3 className="text-3xl font-bold text-slate-900">{data?.kpis.totalListens.toLocaleString()}</h3>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
          <div className="flex items-start justify-between mb-4">
            <div className="p-2 bg-slate-50 rounded-lg border border-slate-100">
              <Clock className="text-slate-700" size={20} />
            </div>
            <span className="text-xs font-medium text-slate-600 bg-slate-50 px-2 py-1 rounded-full flex items-center gap-1">
              Stable
            </span>
          </div>
          <div>
            <p className="text-sm font-medium text-slate-500 mb-1">Avg. Completion Rate</p>
            <h3 className="text-3xl font-bold text-slate-900">{data?.kpis.averageCompletionRate}%</h3>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
          <div className="flex items-start justify-between mb-4">
            <div className="p-2 bg-slate-50 rounded-lg border border-slate-100">
              <Users className="text-slate-700" size={20} />
            </div>
            <span className="text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full flex items-center gap-1">
              <TrendingUp size={12} />
              +5%
            </span>
          </div>
          <div>
            <p className="text-sm font-medium text-slate-500 mb-1">Active Users</p>
            <h3 className="text-3xl font-bold text-slate-900">{data?.kpis.activeUsers.toLocaleString()}</h3>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-6 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <BarChart3 className="text-slate-400" size={18} />
            Top Performing Stories
          </h2>
        </div>
        <div className="p-6">
          {data.topStories.length === 0 ? (
            <p className="text-slate-400 text-sm text-center py-6">No story performance data yet.</p>
          ) : (
          <div className="space-y-6">
            {data.topStories.map((story, index) => {
              // Calculate width relative to the top story (assuming array is sorted)
              const maxListens = data.topStories[0].listens || 1;
              const widthPercentage = Math.max((story.listens / maxListens) * 100, 5);

              return (
                <div key={story.id} className="group relative">
                  <div className="flex justify-between items-end mb-2">
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-medium text-slate-400 w-4">{index + 1}.</span>
                      <span className="text-sm font-medium text-slate-700 group-hover:text-slate-900 transition-colors">
                        {story.title}
                      </span>
                    </div>
                    <div className="text-right flex items-center gap-4">
                      <span className="text-xs text-slate-500">
                        {story.completionRate}% completion
                      </span>
                      <span className="text-sm font-medium text-slate-900 w-16">
                        {story.listens.toLocaleString()}
                      </span>
                    </div>
                  </div>
                  <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-slate-800 rounded-full transition-all duration-1000 ease-out"
                      style={{ width: `${widthPercentage}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
          )}
        </div>
      </div>
    </div>
  );
}
